import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Typography, Button, Alert, Box, Paper, Card, CardContent,
} from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface TenantInfo {
  id: number;
  name: string;
  slug: string;
  agency_logo_url: string | null;
  timezone: string;
}

export default function BrandingPage() {
  const { tenantId } = useAuth();
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const res = await api.get(`/api/tenants/${tenantId}`);
      setTenant(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load tenant info');
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !tenantId) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    setError(null);
    try {
      await api.post(`/api/tenants/${tenantId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess('Logo uploaded successfully');
      setTimeout(() => setSuccess(null), 3000);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!tenantId || !window.confirm('Remove the agency logo?')) return;
    try {
      await api.put(`/api/tenants/${tenantId}`, { agency_logo_url: null });
      setSuccess('Logo removed');
      setTimeout(() => setSuccess(null), 3000);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove logo');
    }
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>Branding</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Customize your agency branding. The logo appears on the login page and portal header.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3, maxWidth: 600 }}>
        <Typography variant="h6" gutterBottom>Agency Name</Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>{tenant?.name || 'Loading...'}</Typography>

        <Typography variant="h6" gutterBottom>Agency Logo</Typography>

        {tenant?.agency_logo_url ? (
          <Card sx={{ mb: 2, p: 2, textAlign: 'center', bgcolor: 'background.default' }}>
            <CardContent>
              <img
                src={tenant.agency_logo_url}
                alt="Agency logo"
                style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'contain' }}
              />
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ mb: 2, p: 4, textAlign: 'center', border: '2px dashed', borderColor: 'divider', borderRadius: 1 }}>
            <Typography color="text.secondary">No logo uploaded</Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
            style={{ display: 'none' }}
            onChange={handleUpload}
          />
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Logo'}
          </Button>
          {tenant?.agency_logo_url && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={handleRemoveLogo}
            >
              Remove Logo
            </Button>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Accepted formats: PNG, JPEG, GIF, SVG, WebP. Max size: 5 MB.
        </Typography>
      </Paper>
    </>
  );
}
